import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { GeneralSelectionFacade } from "../../general-selection/facade/general-selection.facade";
import { computed, Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, filter, map, Observable, of } from "rxjs";
import {
    ICashflowAvailableData,
    ICashFlowAvailableDataObject,
    ICashflowAvailableDataResult,
    ICashflowRequest,
} from "src/app/models/cashflow";
import { CashflowService } from "src/app/services/http/cashflow.service";
import { toSignal } from "@angular/core/rxjs-interop";
import { AppConfig } from "../../../app.config";
import { sharedDeduplicatingSwitchMap } from "../../../utils/deduplicatingSwitchMap";
import { catchError, shareReplay, startWith } from "rxjs/operators";
import { SnackBarService } from "../../../services/snackbar/snackBar.service";

/**
 * This faacde is responsible for managing the latest cashflow data (as at 'todays' date).
 * 
 * It should be used in scenarios where the latest cashflow data is required - for 
 * example, when performing financial actions such as trading or withdrawals.
 * 
 * See the CashflowFacade for cashflow data as at the date in the general selection state.
 */
@UntilDestroy()
@Injectable()
export class LatestCashflowFacade {
    private nudgeLatesttCashflowData$ = new BehaviorSubject<boolean>(true);

    private latestCashflowRequestModel$ = combineLatest([
        this.generalSelectionFacade.getSelectedEntityPortfolioState$,
        this.nudgeLatesttCashflowData$.asObservable(),
    ]).pipe(
        untilDestroyed(this),
        map(([generalState]) => {
            return {
                clientEntities: generalState.selectedEntities,
                date: new Date(),
                portfolios: generalState.selectedPortfolios,
            } as ICashflowRequest;
        }),
    );

    /**
     * The latest cashflow data as at today's date.
     */
    public latestCashflowData$: Observable<ICashflowAvailableDataResult> = this.latestCashflowRequestModel$.pipe(
        untilDestroyed(this),
        filter((request) => {
            return this.shouldRequestLatestCashflow(request);
        }),
        map((data: ICashflowRequest) => {
            const dateWithoutTime = data.date
                ? new Date(data.date.getFullYear(), data.date.getMonth(), data.date.getDate())
                : null;
            return {
                ...data,
                date: dateWithoutTime,
            };
        }),
        sharedDeduplicatingSwitchMap((request: ICashflowRequest) => {
            return this.cashflowService.getCashAvailableData(request).pipe(
                catchError((error) => {
                    this.snackBarService.showWarningSnackBar("Cashflow available data couldn't be loaded.");
                    return of({
                        cashflowAvailableData: null as ICashflowAvailableData,
                        date: null,
                        clientEntities: [],
                        portfolios: [],
                        error: true,
                        loading: false,
                    } as ICashflowAvailableDataResult);
                }),
                map((data: ICashflowAvailableData) => {
                    return {
                        cashflowAvailableData: data,
                        date: request.date,
                        clientEntities: request.clientEntities,
                        portfolios: request.portfolios,
                        error: false,
                        loading: false,
                    } as ICashflowAvailableDataResult;
                }),
                startWith({
                    cashflowAvailableData: null as ICashflowAvailableData,
                    date: null,
                    clientEntities: [],
                    portfolios: [],
                    error: false,
                    loading: true,
                } as ICashflowAvailableDataResult),
            );
        }),
        shareReplay(1),
    );

    public latestCashflowByCurrency$: Observable<ICashFlowAvailableDataObject[]> = this.latestCashflowData$.pipe(
        filter((data: ICashflowAvailableDataResult) => this.isCashflowDataPopulated(data)),
        map((c) => {
            const children = c?.cashflowAvailableData?.children ?? [];

            const currencyData = children.flatMap((child) => child.children).map((c) => c.data);

            return this.aggregateLatestCashByCurrency(currencyData);
        }),
    );

    /**
     *  Signal representation of CURRENT cashFlowData$
     */
    public latestCashflow = toSignal(
        this.latestCashflowData$.pipe(
            untilDestroyed(this),
            filter((result) => this.isCashflowDataPopulated(result)),
            map((result) => result.cashflowAvailableData.data),
        ),
    );

    /**
     *  Signal representation of cashflowByCurrency
     */
    private cashflowByCurrency = toSignal(this.latestCashflowByCurrency$);

    protected hasAndSupportsMultipleCurrenciesForTrading = computed(
        () =>
            this.cashflowByCurrency().length > 1 &&
            this.appConfig.appConfigSignal()?.features?.buySell?.allowMultipleCurrencies,
    );

    constructor(
        private generalSelectionFacade: GeneralSelectionFacade,
        private cashflowService: CashflowService,
        private appConfig: AppConfig,
        private snackBarService: SnackBarService,
    ) {}

    private shouldRequestLatestCashflow(request: ICashflowRequest) {
        if (request == null) return false;

        if ((request.portfolios?.length ?? 0) == 0 && (request?.clientEntities?.length ?? 0) == 0) return false;

        // if we have a mismatch on our entities and portfolios then don't request anything
        if (request.portfolios && request.portfolios.length > 0) {
            let entityIds = request.clientEntities.map((e) => e.EntityId);
            if (!request.portfolios.some((p) => entityIds.includes(p.entityId))) return false;
        }

        return true;
    }

    /**
     * Requests cashflow available data for values set in the general selection state
     */
    public requestLatestAvailableCashflowData(): void {
        this.nudgeLatesttCashflowData$.next(true);
    }

    /**
     * Gets the available cash for trading. If the portal supports multiple currencies, then the available cash
     * for the specified is returned. If the portal does not support multiple currencies, then the available cash
     * in the portfolios reference currency is returned.
     */
    public getLatestAvailableCashForTrading(currency: string): ICashFlowAvailableDataObject {
        const cashflowRoot = this.latestCashflow();
        const breakdowns = this.cashflowByCurrency();

        if (!this.hasAndSupportsMultipleCurrenciesForTrading()) {
            return cashflowRoot;
        }

        return breakdowns.find((b) => b.CurrencyName === currency);
    }

    /**
     * Aggregates cash across all entities and portfolios by currency.
     */
    private aggregateLatestCashByCurrency(records: ICashFlowAvailableDataObject[]): ICashFlowAvailableDataObject[] {
        const grouped: Record<number, ICashFlowAvailableDataObject> = {};

        for (const record of records) {
            const currencyKey = record.Currency ?? -1;

            if (!grouped[currencyKey]) {
                // EntityId and PortfolioId are zero because these cash balances are technically aggregated across
                // portfolios if you have multiple
                grouped[currencyKey] = {
                    EntityId: 0,
                    PortfolioId: 0,
                    Caption: record.CurrencyName,
                    Currency: record.Currency,
                    CurrencyName: record.CurrencyName,
                    CashAvailable: 0,
                    CashBalances: 0,
                    IncomePending: 0,
                    OutgoingsPending: 0,
                    UnsettledTrades: 0,
                    QuarantinedCash: 0,
                };
            }

            grouped[currencyKey].CashAvailable += record.CashAvailable ?? 0;
            grouped[currencyKey].CashBalances += record.CashBalances ?? 0;
            grouped[currencyKey].IncomePending += record.IncomePending ?? 0;
            grouped[currencyKey].OutgoingsPending += record.OutgoingsPending ?? 0;
            grouped[currencyKey].UnsettledTrades += record.UnsettledTrades ?? 0;
            grouped[currencyKey].QuarantinedCash += record.QuarantinedCash ?? 0;
        }

        return Object.values(grouped);
    }

    /**
     * Checks if cashflow data is properly populated
     */
    private isCashflowDataPopulated(cashflowAvailableResult: ICashflowAvailableDataResult): boolean {
        return cashflowAvailableResult.cashflowAvailableData?.children?.length > 0;
    }
}
